import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { CmsService, RoutingService } from '@spartacus/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { CustomAddToCartService } from '../custom-add-to-cart/custom-add-to-cart.service';
import { PriceChannel } from '../shared/model/price-channel.model';

const XLS_EXT = '.xlsx';
const CSV_EXT = '.csv';
const ALLOWED_EXTENSIONS = [XLS_EXT, CSV_EXT];

@Component({
  selector: 'app-import-cart',
  templateUrl: './import-cart.component.html',
  styleUrls: ['./import-cart.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ImportCartComponent implements OnInit, OnDestroy {
  showSuccessMessage: boolean = false;
  fileCorrect: boolean = false;
  fileName: string = '';
  file: File;
  selectedChannel: string;
  titleShouldShow: boolean = true;
  error: string = '';
  fileUploadForm = new FormGroup({
    fileUpload: new FormControl(''),
  });
  subCms: Subscription;

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly addToCart: CustomAddToCartService,
    private readonly cmsService: CmsService,
    private readonly router: RoutingService
  ) {
  }

  ngOnInit(): void {
    this.selectedChannel = sessionStorage.getItem('selectedChannel');
    this.subCms = this.cmsService //hide-show page title, depending on page
      .getCurrentPage()
      .pipe(take(1))
      .subscribe((page) => {
        this.titleShouldShow = page?.pageId !== 'import-cart';
      });

    if (this.selectedChannel === PriceChannel.PUBLIC) {
      this.router.goByUrl('my-account/account-overview');
    }
    this.ref.detectChanges();
  }

  exportCSV(): void {
    this.addToCart.downloadImportCartTemplate();
  }

  exportXLSX(): void {
    this.addToCart.downloadImportCartTemplatexls();
  }

  choosefile(event): void {
    const file: File = event.target.files[0];

    if (!file) {
      return;
    }

    this.fileName = file.name;
    this.file = file;
  }

  importcsvxls(): void {
    this.sendFile();
  }

  async sendFile(): Promise<void> {
    if (this.file) {
      this.showSuccessMessage = false;
      await this.validateFile(this.file);
      if (this.fileCorrect) {
        this.addToCart
          .importCart(this.file)
          .pipe(take(1))
          .subscribe((response) => {
            if (201 === response.status) {
              this.fileName = '';
              this.showSuccessMessage = true;
              this.fileUploadForm.reset();
              this.ref.detectChanges();
            }
          });
      }
    } else {
      this.error = 'importCart.noFileSelectedError';
      this.ref.detectChanges();
    }
  }

  getHeaderRow(sheet): any[] {
    const headers = [];
    const range = XLSX.utils.decode_range(sheet['!ref']);
    let C;
    const R = range.s.r; /* start in the first row */
    /* walk every column in the range */
    for (C = range.s.c; C <= range.e.c; ++C) {
      const cell =
        sheet[
          XLSX.utils.encode_cell({ c: C, r: R })
          ]; /* find the cell in the first row */
      let hdr = 'UNKNOWN ' + C; // <-- replace with your desired default
      if (cell && cell.t) {
        hdr = XLSX.utils.format_cell(cell);
        headers.push(hdr);
      }
    }
    return headers;
  }

  async validateFile(file: File): Promise<void> {
    this.error = '';
    this.fileCorrect = true;
    const fileReader = new FileReader();
    const exceltoJson = {};
    const headerJson = {};
    const isAllowedExtension = this.isAllowedExtension(this.fileName);

    await new Promise((resolve, reject) => {
      fileReader.onload = () => {
        if (this.fileName.toLowerCase().endsWith(XLS_EXT)) {
          fileReader.readAsBinaryString(file);
          exceltoJson['filename'] = file;
          fileReader.onload = (e: any) => {
            /* create workbook */
            const binarystr: string = e.target.result;
            const wb: XLSX.WorkBook = XLSX.read(binarystr, { type: 'binary' });
            for (let i = 0; i < wb.SheetNames.length; ++i) {
              const wsname: string = wb.SheetNames[i];
              const ws: XLSX.WorkSheet = wb.Sheets[wsname];
              const data = XLSX.utils.sheet_to_json(ws); // to get 2d array pass 2nd parameter as object {header: 1}
              exceltoJson[`sheet${i + 1}`] = data;
              const tableHeaders = this.getHeaderRow(ws);
              headerJson[`header${i + 1}`] = tableHeaders;
            }
            exceltoJson['headers'] = headerJson;
            const headers = exceltoJson['headers']['header1'];
            const requiredColumns = ['EAN', 'Nome do Produto', 'Qtde'];
            const missingColumns = requiredColumns.filter((column) => {
              return !headers.find((header) =>
                header.toLowerCase().startsWith(column.toLowerCase())
              );
            });
            if (!isAllowedExtension) {
              this.error = 'importCart.incorrectFileFormatError';
              this.fileCorrect = false;
            } else {
              if (headers.length < 3) {
                this.error = 'importCart.numberOfColumnsError';
                this.fileCorrect = false;
              } else if (missingColumns.length > 0) {
                this.error = 'importCart.incorrectColumnsError';
                this.fileCorrect = false;
              }
            }
            this.ref.detectChanges();
            resolve(file);
          };
        } else {
          const csvString = fileReader.result as string;
          const rows = csvString.split(/[\r\n]/g);
          const headers = rows[0].split(/[;|,]/g);
          const requiredColumns = ['EAN', 'Nome do Produto', 'Qtde'];
          const missingColumns = requiredColumns.filter((column) => {
            return !headers.find((header) =>
              header.toLowerCase().startsWith(column.toLowerCase())
            );
          });

          if (!isAllowedExtension) {
            this.error = 'importCart.incorrectFileFormatError';
            this.fileCorrect = false;
            this.file = null;
          } else {
            if (headers.length < 3) {
              this.error = 'importCart.numberOfColumnsError';
              this.fileCorrect = false;
            } else if (missingColumns.length > 0) {
              this.error = 'importCart.incorrectColumnsError';
              this.fileCorrect = false;
            }
          }
          this.ref.detectChanges();
          resolve(file);
        }
      };
      fileReader.readAsText(file);
    });
  }

  ngOnDestroy(): void {
    this.subCms?.unsubscribe();
  }

  private isAllowedExtension(fileName: string): boolean {
    return ALLOWED_EXTENSIONS.some(e => fileName.toLowerCase().includes(e));
  }
}
